const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v2.pnsynd.com/api';
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'development';

export const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
};

export const API_ENDPOINTS = {
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
    changePassword: '/auth/change-password',
    resetPassword: '/auth/reset-password',
    validate: '/auth/validate',
  },

  applications: {
    list: '/apps',
    create: '/apps',
    get: (id: number) => `/apps/${id}`,
    update: (id: number) => `/apps/${id}`,
    delete: (id: number) => `/apps/${id}`,
    listUsers: (id: number) => `/apps/${id}/users`,
    getActiveUsers: (id: number) => `/apps/${id}/users/active`,
    getDeletedUsers: (id: number) => `/apps/${id}/users/deleted`,
    getUserStats: (id: number) => `/apps/${id}/users/stats`,
    getUser: (appId: number, userId: number) => `/apps/${appId}/users/${userId}`,
    deleteUser: (appId: number, userId: number) => `/apps/${appId}/users/${userId}`,
    restoreUser: (appId: number, userId: number) => `/apps/${appId}/users/${userId}/restore`,
    exportUsers: (id: number) => `/apps/${id}/users/export`,
    copy: (id: number) => `/apps/${id}/copy`,
    grantAccess: '/apps/access/grant',
    revokeAccess: '/apps/access/revoke',
  },

  push: {
    send: '/push/send',
    sendTest: '/push/test',
    getStatus: (messageId: string) => `/push/${messageId}/status`,
    history: '/push/history',
  },

  campaigns: {
    list: '/campaigns',
    create: '/campaigns',
    get: (id: number) => `/campaigns/${id}`,
    update: (id: number) => `/campaigns/${id}`,
    delete: (id: number) => `/campaigns/${id}`,
  },

  globalCampaigns: {
    list: '/global/campaigns',
    create: '/global/campaigns',
    get: (id: number) => `/global/campaigns/${id}`,
    update: (id: number) => `/global/campaigns/${id}`,
    delete: (id: number) => `/global/campaigns/${id}`,
  },

  segments: {
    list: '/segments',
    create: '/segments',
    get: (id: number) => `/segments/${id}`,
    update: (id: number) => `/segments/${id}`,
    delete: (id: number) => `/segments/${id}`,
    test: '/segments/test',
  },

  globalSegments: {
    list: '/global/segments',
    create: '/global/segments',
    get: (id: number) => `/global/segments/${id}`,
    update: (id: number) => `/global/segments/${id}`,
    delete: (id: number) => `/global/segments/${id}`,
    test: '/global/segments/test',
  },

  stats: {
    dashboard: '/stats/dashboard',
    appStats: (id: number) => `/stats/app/${id}`,
    pushStats: (messageId: string) => `/stats/push/${messageId}`,
    pushReport: (messageId: string) => `/stats/push/${messageId}/report`,
    campaignStats: (id: number) => `/stats/campaign/${id}`,
    conversions: '/stats/conversions',
    userAnalytics: '/stats/users/analytics',
    pushPerformance: '/stats/push/performance',
    export: '/stats/export',
    compareCampaigns: '/stats/compare/campaigns',
    compareSegments: '/stats/compare/segments',
  },

  abTests: {
    list:            '/ab-tests',
    create:          '/ab-tests',
    get:        (id: number) => `/ab-tests/${id}`,
    update:     (id: number) => `/ab-tests/${id}`,
    delete:     (id: number) => `/ab-tests/${id}`,
    start:      (id: number) => `/ab-tests/${id}/start`,
    pause:      (id: number) => `/ab-tests/${id}/pause`,
    complete:   (id: number) => `/ab-tests/${id}/complete`,
    send:       (id: number) => `/ab-tests/${id}/send`,
    sendStatus: (id: number) => `/ab-tests/${id}/send-status`,
    duplicate:  (id: number) => `/ab-tests/${id}/duplicate`,
    results:    (id: number) => `/ab-tests/${id}/results`,
    timeseries: (id: number) => `/ab-tests/${id}/timeseries`,
    recipients: (id: number) => `/ab-tests/${id}/recipients`,
    recipientsExport: (id: number) => `/ab-tests/${id}/recipients/export`,
    previewAudience: '/ab-tests/preview-audience',
  },

  legacy: {
    addAPAUser: '/publicapa/add-user',
    updateAPAReg: (afId: string, reg: number) => `/publicapa/update-reg/${afId}/${reg}`,
    updateAPADep: (afId: string, dep: number) => `/publicapa/update-dep/${afId}/${dep}`,
    addPWAUser: '/pwauser/add-user',
    updatePWAUser: (userId: string) => `/pwauser/update-user/${userId}`,
    updatePWAReg: (userId: string, reg: number, offer: string) => `/pwauser/update-reg/${userId}/${reg}/${offer}`,
    updatePWADep: (userId: string, dep: number, offer: string) => `/pwauser/update-dep/${userId}/${dep}/${offer}`,
    getPushInteraction: (messageId: string) => `/interaction/${messageId}`,
    updatePushInteraction: (messageId: string) => `/interaction/${messageId}`,
  },
};

export const APP_CONFIG = {
  appName: 'Push Notification System',
  version: process.env.REACT_APP_VERSION || '1.0.0',
  environment: ENVIRONMENT,
  isDevelopment: ENVIRONMENT === 'development',
  isProduction: ENVIRONMENT === 'production',
  tokenKey: 'access_token',
  refreshTokenKey: 'refresh_token',
  defaultPageSize: 20,
  pageSizeOptions: [10, 20, 50, 100],
  maxFileSize: 5 * 1024 * 1024,
  maxExportRows: 10000,
  maxBatchSize: 500,
  requestTimeout: 30000,
  refreshTokenTimeout: 7 * 24 * 60 * 60 * 1000,
  rateLimitPerMinute: 100,
  attributionWindowMinutes: 90,
};

export const DATE_FORMAT = {
  display: 'DD.MM.YYYY',
  displayWithTime: 'DD.MM.YYYY HH:mm',
  api: 'YYYY-MM-DD',
  apiWithTime: 'YYYY-MM-DDTHH:mm:ss',
};